import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Alert, Button} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";
import ImportIdentityReview from "@/components/ImportIdentityReview";
import {getDsContext} from "@/utils/dsContext";
import OwnClubLink from "@/components/OwnClubLink";
import {CustomFootballImportAPI} from "@/api/customFootballImport";
import {prepareCustomFootballImport} from "@/utils/customFootballImport";

export default function ImportCompetitionData() {
	useSelector((s) => s.user);
	useSelector((s) => s.token);
	useSelector((s) => s.season?.id);
	return <ImportCompetitionDataSession key={getDsContext()} />;
}
function ImportCompetitionDataSession() {
	const navigate = useNavigate();
	const seasons = useSelector((s) => s?.seasons || []);
	const currentSeason = useSelector((s) => s?.season?.id);
	const [seasonId, setSeasonId] = useState(currentSeason || ""),
		[files, setFiles] = useState([]),
		[bundle, setBundle] = useState(null),
		[options, setOptions] = useState(null),
		[ownKey, setOwnKey] = useState(null),
		[rosters, setRosters] = useState({}),
		[identityReview, setIdentityReview] = useState(null),
		[selections, setSelections] = useState({
			player: {},
			club: {},
			competition: {},
		}),
		[newPlayers, setNewPlayers] = useState([]),
		[acknowledge, setAcknowledge] = useState(false),
		[preview, setPreview] = useState(null),
		[result, setResult] = useState(null),
		[busy, setBusy] = useState(false),
		[error, setError] = useState(""),
		[linkClub, setLinkClub] = useState(null);
	useEffect(() => {
		let active = true;
		setOptions(null);
		CustomFootballImportAPI.options(seasonId)
			.then((d) => {
				if (active) setOptions(d);
			})
			.catch((e) => {
				if (active)
					setError(e.response?.data?.error || "Opzioni non disponibili.");
			});
		return () => {
			active = false;
		};
	}, [seasonId]);
	const families = useMemo(
		() => [...new Set(bundle?.teams.map((t) => t.clubKey) || [])].sort(),
		[bundle],
	);
	const ownTeams = bundle?.teams.filter((t) => t.clubKey === ownKey) || [];
	const existingClub = options?.clubs.find(
		(c) => c.name === ownKey && c.id !== options.ownClub?.id,
	);
	const alreadyLinked =
		existingClub && options?.linkedClubIds.includes(existingClub.id);
	const body = () => ({
		...bundle,
		seasonId,
		ownClubKey: ownKey,
		existingPlayers: Object.fromEntries(
			Object.entries(selections.player).filter(([, v]) => !!v),
		),
		existingClubs: Object.fromEntries(
			Object.entries(selections.club).filter(([, v]) => !!v),
		),
		existingCompetitions: Object.fromEntries(
			Object.entries(selections.competition).filter(([, v]) => !!v),
		),
		createNewPlayers: newPlayers,
		acknowledgeConflicts: acknowledge,
		conflictToken: identityReview?.conflictToken,
		existingRosters: Object.fromEntries(
			Object.entries(rosters).filter(
				([key, value]) => value && ownTeams.some((t) => t.externalId === key),
			),
		),
	});
	const read = async () => {
		setBusy(true);
		setError("");
		setPreview(null);
		setResult(null);
		try {
			setIdentityReview(null);
			setSelections({player: {}, club: {}, competition: {}});
			setNewPlayers([]);
			setAcknowledge(false);
			setBundle(await prepareCustomFootballImport(files, seasonId));
			setOwnKey(null);
			setRosters({});
		} catch (e) {
			setBundle(null);
			setError(e.message);
		} finally {
			setBusy(false);
		}
	};
	const verify = async () => {
		setBusy(true);
		setError("");
		try {
			const data = await CustomFootballImportAPI.preview(body());
			setPreview(data);
			setIdentityReview(data);
			setAcknowledge(false);
		} catch (e) {
			setPreview(null);
			setError(e.response?.data?.error || "Verifica non riuscita.");
		} finally {
			setBusy(false);
		}
	};
	const apply = async () => {
		setBusy(true);
		setError("");
		try {
			setResult(await CustomFootballImportAPI.apply(body()));
			setPreview(null);
		} catch (e) {
			setPreview(null);
			if (e.response?.data?.identities) setIdentityReview(e.response.data);
			setAcknowledge(false);
			setError(e.response?.data?.error || "Importazione non riuscita.");
		} finally {
			setBusy(false);
		}
	};
	const invalidate = () => {
		setPreview(null);
		setResult(null);
		setAcknowledge(false);
	};
	const chooseIdentity = (kind, key, id, fresh) => {
		setSelections((prev) => ({...prev, [kind]: {...prev[kind], [key]: id}}));
		if (kind === "player")
			setNewPlayers((prev) => [
				...prev.filter((k) => k !== key),
				...(fresh ? [key] : []),
			]);
		invalidate();
	};
	const labels = {
		ImportedPlayerCareer: "Righe carriera importata",
		Competitions: "Competizioni",
		CompetitionsSeasons: "Edizioni",
		Clubs: "Club",
		Rosters: "Rose",
		Players: "Giocatori",
		Matches: "Partite",
		CompetitionsSeasonsGroups: "Gironi",
		CompetitionsSeasonsGroupsRounds: "Giornate",
	};
	return (
		<Page>
			<Breadcrumbs
				elements={[
					{
						label: "Competizioni",
						path: "/competitions",
					},
					{label: "Importa JSON"},
				]}
				actions={
					<MainActions
						actions={[
							{
								icon: <ArrowBackIcon />,
								onClick: () => navigate("/competitions"),
							},
						]}
					/>
				}
			/>
			<div className="p-3">
				<h3>Importa competizioni, rose e partite</h3>
				<p>
					Seleziona i file squadre, giocatori e partite delle competizioni da
					caricare. Nessun dato cambia fino alla conferma.
				</p>
				{error && <Alert severity="error">{error}</Alert>}
				<div className="row mb-3">
					<div className="col-md-6">
						<VLFSelectNew
							label="Stagione di destinazione"
							options={seasons.map((s) => ({value: s.id, label: s.name}))}
							value={
								seasons.find((s) => s.id === seasonId)
									? {
											value: seasonId,
											label: seasons.find((s) => s.id === seasonId).name,
										}
									: null
							}
							onChange={(s) => {
								setSeasonId(s?.value || "");
								setBundle(null);
								setIdentityReview(null);
								invalidate();
							}}
							editable={!busy}
						/>
					</div>
					<div className="col-md-6">
						<label className="d-block">File JSON</label>
						<input
							aria-label="File JSON"
							type="file"
							accept=".json"
							multiple
							disabled={busy}
							onChange={(e) => {
								setFiles([...e.target.files]);
								setBundle(null);
								setIdentityReview(null);
								invalidate();
							}}
						/>
						<div>{files.length} file selezionati</div>
					</div>
				</div>
				<Button
					variant="outlined"
					disabled={busy || !files.length || !seasonId}
					onClick={read}
				>
					Prepara anteprima
				</Button>
				{bundle && (
					<>
						<p className="mt-3">
							{bundle.competitions.length} competizioni · {families.length} club
							· {bundle.teams.length} rose · {bundle.players.length} giocatori ·{" "}
							{bundle.matches.length} partite
						</p>
						<VLFSelectNew
							label="Quale club è il tuo?"
							isClearable
							options={families.map((f) => ({value: f, label: f}))}
							value={ownKey ? {value: ownKey, label: ownKey} : null}
							placeholder="Nessuno: tutti i club sono esterni"
							onChange={(c) => {
								setOwnKey(c?.value || null);
								setRosters({});
								invalidate();
							}}
							editable={!busy}
						/>
						{ownKey && (
							<>
								<p className="mt-2">
									Club della tua sessione:{" "}
									<strong>{options?.ownClub?.name}</strong>
								</p>
								{existingClub && !alreadyLinked ? (
									<Alert severity="info">
										Il club è già caricato. Collegalo prima di ripetere
										l’importazione.{" "}
										<Button
											disabled={busy}
											onClick={() => setLinkClub(existingClub.id)}
										>
											Collega al mio club
										</Button>
									</Alert>
								) : (
									<>
										{alreadyLinked && (
											<Alert severity="info">
												Club già collegato: verranno riutilizzate le
												associazioni confermate.
											</Alert>
										)}
										{!alreadyLinked &&
											ownTeams.map((t) => (
												<div
													className="row align-items-center mb-3"
													key={t.externalId}
												>
													<div className="col-md-5">
														<strong>{t.name}</strong>
													</div>
													<div className="col-md-7">
														<VLFSelectNew
															label="Rosa interna"
															options={[
																{value: "new", label: "Crea nuova rosa"},
																...(options?.internalRosters || []).map(
																	(r) => ({value: r.id, label: r.name}),
																),
															]}
															value={
																rosters[t.externalId]
																	? {
																			value: rosters[t.externalId],
																			label: options?.internalRosters.find(
																				(r) => r.id === rosters[t.externalId],
																			)?.name,
																		}
																	: {value: "new", label: "Crea nuova rosa"}
															}
															onChange={(r) => {
																setRosters((prev) => ({
																	...prev,
																	[t.externalId]:
																		r?.value === "new" ? null : r?.value,
																}));
																invalidate();
															}}
															editable={!busy}
														/>
													</div>
												</div>
											))}
									</>
								)}
							</>
						)}
						<details className="my-3">
							<summary>Club e rose da importare</summary>
							{families.map((f) => (
								<p key={f}>
									<strong>{f}</strong>:{" "}
									{bundle.teams
										.filter((t) => t.clubKey === f)
										.map((t) => t.name)
										.join(", ")}
								</p>
							))}
						</details>
						{!!bundle.warnings.length && (
							<details className="mb-3">
								<summary>
									Segnalazioni da controllare ({bundle.warnings.length})
								</summary>
								{bundle.warnings.map((w, i) => (
									<p key={i}>{w}</p>
								))}
							</details>
						)}
						<p>
							Gli orari mancanti restano da definire; risultati mancanti restano
							vuoti. Questi file non creano distinte, eventi o dati completi
							delle partite.
						</p>
						<Button
							variant="outlined"
							disabled={busy || !options || (existingClub && !alreadyLinked)}
							onClick={verify}
						>
							Verifica importazione
						</Button>
						{identityReview && (
							<ImportIdentityReview
								review={identityReview}
								selections={selections}
								newPlayers={newPlayers}
								onChange={chooseIdentity}
								acknowledge={acknowledge}
								onAcknowledge={setAcknowledge}
								busy={busy}
							/>
						)}
						{preview && (
							<Alert className="mt-3" severity="warning">
								<p>
									Aggiunte previste:{" "}
									{Object.entries(preview.created)
										.filter(([k]) => labels[k])
										.map(([k, v]) => `${labels[k]}: ${v}`)
										.join(" · ") || "nessun nuovo dato"}
									. I dati già presenti saranno conservati.
								</p>
								<Button
									variant="contained"
									disabled={
										busy ||
										preview.identities?.some(
											(r) => r.status === "unresolved",
										) ||
										(!!preview.conflicts?.length && !acknowledge)
									}
									onClick={apply}
								>
									Conferma importazione
								</Button>
							</Alert>
						)}
						{result && (
							<Alert className="mt-3" severity="success">
								Importazione completata. {result.totalPlayers} giocatori e{" "}
								{result.totalMatches} partite verificati. Nessun duplicato delle
								righe già presenti.
							</Alert>
						)}
					</>
				)}
				{busy && <p>Operazione in corso…</p>}
			</div>
			{linkClub && (
				<OwnClubLink
					clubId={linkClub}
					seasonId={seasonId}
					close={() => setLinkClub(null)}
					onLinked={async () => {
						setLinkClub(null);
						invalidate();
						setOptions(await CustomFootballImportAPI.options(seasonId));
					}}
				/>
			)}
		</Page>
	);
}
